import { NodeId } from 'react-accessible-treeview/dist/TreeView/types';
import { createListenerMiddleware, createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { defaultIndexDef } from 'constants/data-tools';
import { readStorageValue, saveStorageValue } from 'hooks/use-storage/use-storage.utils';
import { AnalyserStates, IndexDef } from 'types/fts';
import { QueryTabs } from 'types/query-result';
import { getStorageValuesWithPrefix } from 'utils/storage';
import { RootState } from './root.types';

export const datatoolsName = 'datatools';
export const datatoolsStorageKey = 'cb:workbenchOptions_';
export const eventingErrorsCountKey = 'eventing-errors-count';

type CommonActionPayload = {
  databaseId: string;
};

export type ExpandableItem = {
  bucket: string;
  scope: string;
  collection: string;
};

export type ExpandedNodes = Record<NodeId, boolean>;

export type DatatoolsOptions = {
  selectedItems: ExpandableItem;
  expanded?: ExpandedNodes;
  leftPanelHidden?: boolean;
  rightPanelHidden?: boolean;
};

type DatatoolsSlice = {
  datatoolsById: Record<string, DatatoolsOptions>;
  activeQueryResultTab: QueryTabs;
  functionErrorCountById: Record<string, number>;
  indexDef: IndexDef;
  tabOpened: AnalyserStates;
  dateTimeParser: boolean;
  isAdvanced: boolean;
};

const getInitialState = () => {
  const workbenchOptions = getStorageValuesWithPrefix(datatoolsStorageKey);
  return workbenchOptions.reduce<DatatoolsSlice>(
    (initialState, { id, value }) => {
      initialState.datatoolsById[id] = value;
      return initialState;
    },
    {
      indexDef: defaultIndexDef,
      isAdvanced: false,
      datatoolsById: {},
      activeQueryResultTab: QueryTabs.JSON,
      tabOpened: {
        isOpen: false,
        tabName: '',
        shouldUpdate: false,
        analyserName: '',
        clearForm: false,
      },
      dateTimeParser: false,
      functionErrorCountById: readStorageValue({
        storageKey: eventingErrorsCountKey,
        fallbackValue: {},
        validate: (value: unknown): value is Record<string, number> =>
          !!value && typeof value === 'object' && Object.values(value).every((value) => typeof value === 'number'),
      }),
    }
  );
};

export const datatoolsResultSlice = createSlice({
  name: datatoolsName,
  initialState: getInitialState(),
  reducers: {
    resetSearchIndexDefenition(state) {
      state.indexDef = getInitialState().indexDef;
    },
    setSearchIndexDefenition(state, { payload }: PayloadAction<IndexDef>) {
      state.indexDef = payload;
    },
    // set tab opened will have 2 values 1 boolean and 1 string and string is optional write function to handle this
    setTabOpened(state, { payload }: PayloadAction<AnalyserStates>) {
      state.tabOpened = {
        isOpen: payload.isOpen,
        tabName: payload.tabName ?? '', // Set to empty string if tab is not provided
        shouldUpdate: payload.shouldUpdate,
        analyserName: payload.analyserName,
        clearForm: payload.clearForm,
      };
    },
    setDateTimeParserTabOpened(state, { payload }: PayloadAction<boolean>) {
      state.dateTimeParser = payload;
    },
    setIsAdvanced(state, { payload }: PayloadAction<boolean>) {
      state.isAdvanced = payload;
    },

    setFunctionErrorCounters(state, { payload }: PayloadAction<Record<string, number>>) {
      state.functionErrorCountById = payload;
    },
    selectItem(state, { payload: { databaseId, selectedItems } }: PayloadAction<CommonActionPayload & { selectedItems: ExpandableItem }>) {
      state.datatoolsById[databaseId] = { ...state.datatoolsById[databaseId], selectedItems };
    },
    setActiveQueryResultTab(state, { payload }: PayloadAction<QueryTabs>) {
      state.activeQueryResultTab = payload;
    },

    setExpandedNodes(state, { payload: { databaseId, expanded } }: PayloadAction<CommonActionPayload & { expanded: ExpandedNodes }>) {
      state.datatoolsById[databaseId] = {
        ...state.datatoolsById[databaseId],
        expanded: {
          ...state.datatoolsById[databaseId]?.expanded,
          ...expanded,
        },
      };
    },
  },
});

export default datatoolsResultSlice.reducer;
export const {
  selectItem,
  setExpandedNodes,
  setActiveQueryResultTab,
  setFunctionErrorCounters,
  setTabOpened,
  setDateTimeParserTabOpened,
  setSearchIndexDefenition,
  setIsAdvanced,
  resetSearchIndexDefenition,
} = datatoolsResultSlice.actions;

export const getDatabaseDatatools = (state: RootState) => {
  return state[datatoolsName].datatoolsById;
};

export const getSearchIndexDefenition = (state: RootState) => {
  return state[datatoolsName].indexDef;
};
export const getTabOpenedState = (state: RootState) => {
  return state[datatoolsName].tabOpened;
};
export const getTimeParserOpenedState = (state: RootState) => {
  return state[datatoolsName].dateTimeParser;
};

export const getIsAdvancedState = (state: RootState) => {
  return state[datatoolsName].isAdvanced;
};
export const selectFunctionErrorCounters = (state: RootState) => {
  return state[datatoolsName].functionErrorCountById;
};

export const selectActiveQueryResultTab = (state: RootState) => state[datatoolsName].activeQueryResultTab;

export const datatoolsResultMiddleware = createListenerMiddleware();

datatoolsResultMiddleware.startListening({
  matcher: isAnyOf(selectItem, setExpandedNodes),
  effect: (action, listenerApi) => {
    const { databaseId } = (action as PayloadAction<CommonActionPayload>).payload;
    const datatoolsByDatabase = (listenerApi.getState() as RootState)[datatoolsName].datatoolsById[databaseId];

    saveStorageValue({
      storageKey: `${datatoolsStorageKey}${databaseId}`,
      value: { ...datatoolsByDatabase },
    });
  },
});

datatoolsResultMiddleware.startListening({
  matcher: isAnyOf(setFunctionErrorCounters),
  effect: (_action: PayloadAction<Record<string, number>>, listenerApi) => {
    const { functionErrorCountById } = (listenerApi.getState() as RootState)[datatoolsName];

    saveStorageValue({
      storageKey: eventingErrorsCountKey,
      value: functionErrorCountById,
    });
  },
});
